import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from './SafeIcon';
import { createStripeProducts, getStoredPriceIds } from '../services/stripeService';
import * as FiIcons from 'react-icons/fi';

const { FiPackage, FiPlus, FiCheck, FiX, FiDollarSign, FiRefreshCw, FiExternalLink } = FiIcons;

const StripeProductManager = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [priceIds, setPriceIds] = useState({});

  const loadPriceIds = async () => {
    try {
      const storedPriceIds = await getStoredPriceIds();
      setPriceIds(storedPriceIds);
    } catch (error) {
      console.error('Error loading price IDs:', error);
    }
  };

  const handleCreateProducts = async () => {
    setLoading(true);
    setStatus(null);
    
    try {
      const createdProducts = await createStripeProducts();
      setProducts(createdProducts);
      await loadPriceIds(); // Reload price IDs after creation
      
      setStatus({
        type: 'success',
        message: `Successfully created ${createdProducts.length} products in Stripe!`
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: `Error creating products: ${error.message}`
      });
    } finally {
      setLoading(false);
      setTimeout(() => setStatus(null), 10000);
    }
  };

  useEffect(() => {
    loadPriceIds();
  }, []);

  // Canonical catalog — mirrors subscription_plans, landing page,
  // and the Stripe products created by scripts/reset-stripe-catalog.mjs.
  // Starter is free ($0), no Stripe product needed.
  const predefinedProducts = [
    {
      name: 'Snap Chart AI — Pro Trader',
      description: 'Unlimited AI analyses + counterfactual capital tracking + TDI',
      features: ['Unlimited AI analyses', 'Counterfactual capital tracking', 'Trader Discipline Index (TDI)', 'Multi-chart analysis', 'Auto-saved journal'],
      monthlyPrice: '$49',
      yearlyPrice: '$290',
      plan: 'pro_trader',
      stripePriceMonthly: 'price_1TvP2pP3etxjIOy4YgmoGJCR',
      stripePriceYearly: 'price_1TvP2qP3etxjIOy42SPEtOXN',
    },
    {
      name: 'Snap Chart AI — Institutional',
      description: 'Institutional governance, risk officer & audit-ready reporting',
      features: ['Everything in Pro Trader', 'Institutional Review Block', 'Risk Officer Stance', 'Capital Protection Score', 'Priority Support'],
      monthlyPrice: '$149',
      yearlyPrice: '$1,490',
      plan: 'institutional_ai',
      stripePriceMonthly: 'price_1TvP2qP3etxjIOy4xtoaMiht',
      stripePriceYearly: 'price_1TvP2qP3etxjIOy4PixIIngG',
    },
  ];

  const hasConfiguredProducts = Object.keys(priceIds).length > 0;

  return (
    <div className="bg-gray-800 p-6 rounded-lg">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <SafeIcon icon={FiPackage} className="text-green-400 text-2xl" />
          <div>
            <h3 className="text-xl font-bold text-white">Stripe Products & Pricing</h3>
            <p className="text-gray-400 text-sm">Manage your subscription products and prices</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className={`w-3 h-3 rounded-full ${hasConfiguredProducts ? 'bg-green-400' : 'bg-yellow-400'}`}></div>
          <span className="text-sm text-gray-400">
            {hasConfiguredProducts ? 'Products Created' : 'Not Created'}
          </span>
        </div>
      </div>

      {status && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mb-6 p-4 rounded-lg ${
            status.type === 'success' 
              ? 'bg-green-500/10 border border-green-500/20 text-green-300' 
              : 'bg-red-500/10 border border-red-500/20 text-red-300'
          }`}
        >
          <div className="flex items-center space-x-2">
            <SafeIcon icon={status.type === 'success' ? FiCheck : FiX} />
            <span>{status.message}</span>
          </div>
        </motion.div>
      )}

      {/* Products Preview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {predefinedProducts.map((product) => (
          <div key={product.plan} className="bg-gray-700 p-4 rounded-lg border border-gray-600">
            <div className="flex items-start justify-between mb-3">
              <h4 className="font-semibold text-white">{product.name}</h4>
              {priceIds[`${product.plan}_monthly`] && (
                <SafeIcon icon={FiCheck} className="text-green-400" />
              )}
            </div>
            <p className="text-sm text-gray-400 mb-3">{product.description}</p>
            
            <div className="space-y-2 mb-4">
              {product.features.map((feature, index) => (
                <div key={index} className="flex items-center space-x-2 text-xs text-gray-300">
                  <SafeIcon icon={FiCheck} className="text-green-400 w-3 h-3" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>

            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Monthly:</span>
                <span className="text-white font-semibold">{product.monthlyPrice}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Yearly:</span>
                <span className="text-white font-semibold">{product.yearlyPrice}</span>
              </div>
            </div>

            {priceIds[`${product.plan}_monthly`] && (
              <div className="mt-3 p-2 bg-green-500/10 rounded text-xs text-green-300">
                ✓ Created in Stripe
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4">
        <button
          onClick={handleCreateProducts}
          disabled={loading}
          className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white font-bold py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
        >
          <SafeIcon icon={loading ? FiRefreshCw : FiPlus} className={loading ? 'animate-spin' : ''} />
          <span>{loading ? 'Creating Products...' : 'Create Products in Stripe'}</span>
        </button>

        <a
          href="https://dashboard.stripe.com/test/products"
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 bg-gray-600 hover:bg-gray-500 text-white font-bold py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
        >
          <SafeIcon icon={FiExternalLink} />
          <span>View in Stripe Dashboard</span>
        </a>
      </div>

      {/* Price IDs Display */}
      {hasConfiguredProducts && (
        <div className="mt-6 p-4 bg-gray-700/50 rounded-lg">
          <h4 className="font-semibold text-white mb-3">Configured Price IDs:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            {Object.entries(priceIds).map(([key, value]) => (
              <div key={key} className="flex justify-between">
                <span className="text-gray-400">{key}:</span>
                <span className="text-green-300 font-mono text-xs">{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-6 p-4 bg-gray-700/50 rounded-lg border border-gray-600">
        <h4 className="font-semibold text-white mb-2">Instructions:</h4>
        <ol className="text-sm text-gray-300 space-y-1">
          <li>1. Make sure your Stripe secret key is configured above</li>
          <li>2. Click "Create Products in Stripe" to automatically set up all subscription plans</li>
          <li>3. Products will be created with the correct pricing and metadata</li>
          <li>4. Price IDs will be automatically stored in your database</li>
          <li>5. Your pricing page will now work with real Stripe checkout!</li>
          <li>6. Users can subscribe and payments will be processed through Stripe</li>
        </ol>
      </div>
    </div>
  );
};

export default StripeProductManager;